// Lightweight fetch wrapper. All requests carry the session cookie via
// `credentials: 'include'`. In dev the API is proxied at /auth /voice etc.
// (see vite.config.ts). In prod, the API_BASE points at the worker.

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOpts extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export async function api<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { body, headers, ...rest } = opts;
  const init: RequestInit = {
    credentials: 'include',
    headers: {
      ...(body !== undefined && !(body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(headers ?? {}),
    },
    ...rest,
  };
  if (body !== undefined) {
    init.body = body instanceof FormData ? body : JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}${path}`, init);
  const text = await res.text();
  let json: unknown;
  try {
    json = text.length > 0 ? JSON.parse(text) : null;
  } catch {
    json = text;
  }
  if (!res.ok) {
    const errBody = json as { error?: string; message?: string } | null;
    throw new ApiError(errBody?.message ?? `HTTP ${res.status}`, res.status, json);
  }
  return json as T;
}

export function buildWebSocketUrl(path: string): string {
  if (API_BASE) {
    return API_BASE.replace(/^http/, 'ws') + path;
  }
  // Dev: same origin, ws on the proxied path
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}${path}`;
}
